import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Stack,
  Link,
  Divider,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  PersonAddOutlined,
  LockOutlined,
  EmailOutlined,
  PersonOutline,
  PinOutlined,
} from "@mui/icons-material";
import { useNavigate, Link as RouterLink, useSearchParams } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import ApiService from "../services/api";
import { useRegistration } from "../context/RegistrationContext";
import FormErrorSummary from "./shared/FormErrorSummary";
import BrandLogoMark from "./BrandLogoMark";
import {
  SIGNUP_GOAL_OPTIONS,
  SignupGoalId,
  setStoredSignupGoal,
} from "../constants/signupGoals";
import {
  billingPathForPlanIntent,
  clearPendingBillingPlanId,
  getPendingBillingPlanId,
  setPendingBillingPlanId,
} from "../utils/billingPlanIntent";
import { storePendingSupplierInvite } from "../utils/supplierInviteSession";
import { SUBSCRIPTION_PLANS_ENABLED } from "../constants/subscriptionPlans";
import { resolveAuthenticatedHomePath, resolveMsmePostLoginDestination } from "../utils/resolvePostLoginDestination";
import { PARTNER_ORGANIZATION_TYPES, PartnerOrganizationType } from "../constants/partnerProgram";
import { resolveOtpChannels, isValidRegistrationOtp, type PendingLoginOtpState } from "../utils/otpChannels";
import { toNationalIndianMobile } from "../utils/normalizePhoneInput";
import { validatePassword, passwordPolicySummary } from "../utils/passwordPolicy";
import PartnerOnboardingWizard from "./partner/PartnerOnboardingWizard";
import axios from "axios";
import { buildApiUrl } from "../config/apiConfig";

type RegisterAccountType = "msme" | "partner";

// Match your User model fields
type RegisterFormData = {
  email: string;
  password: string;
  confirmPassword: string;
  // role: string;
  firstName: string;
  lastName: string;
  phone: string;
  agreeToTerms: boolean;
  organizationName: string;
  organizationType: PartnerOrganizationType;
};

// Define error type
type FormErrors = {
  email?: string;
  password?: string;
  confirmPassword?: string;
  // role?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  agreeToTerms?: string;
  organizationName?: string;
  organizationType?: string;
};

const UserRegister: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isRegistered, login, completeLoginFromResponse, setAccountPathway } = useRegistration();
  const selectedPlanId = searchParams.get("plan")?.trim() || null;
  const initialAccountType: RegisterAccountType =
    searchParams.get("type")?.trim() === "partner" ? "partner" : "msme";
  const [accountType, setAccountType] = useState<RegisterAccountType>(initialAccountType);
  const REGISTER_DRAFT_KEY = "ci_register_form_draft_v1";
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  const [otpRequired, setOtpRequired] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [otpChannels, setOtpChannels] = useState({ email: false, mobile: true });
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpStatus, setOtpStatus] = useState({
    emailVerified: false,
    mobileVerified: false,
  });
  // Form state - match your User model (restore draft so Terms/Privacy navigation does not wipe fields)
  const [formData, setFormData] = useState<RegisterFormData>(() => {
    const empty: RegisterFormData = {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      phone: "",
      agreeToTerms: false,
      organizationName: "",
      organizationType: "integration_partner",
    };
    try {
      const raw = sessionStorage.getItem(REGISTER_DRAFT_KEY);
      if (!raw) return empty;
      const parsed = JSON.parse(raw) as Partial<RegisterFormData>;
      return {
        ...empty,
        ...parsed,
        // Never restore secrets from a shared browser session draft.
        password: "",
        confirmPassword: "",
        agreeToTerms: false,
      };
    } catch {
      return empty;
    }
  });

  // Validation state
  const [errors, setErrors] = useState<FormErrors>({});
  const [signupGoal, setSignupGoal] = useState<SignupGoalId | null>(null);
  const [partnerWizardOpen, setPartnerWizardOpen] = useState(false);
  const [partnerApiKeyFull, setPartnerApiKeyFull] = useState<string | null>(null);
  // Prevent the "already logged in" effect from overriding onboarding / billing redirects
  // after register → auto-login sets isRegistered.
  const skipRegisteredAutoRedirectRef = useRef(false);

  const resetOtpState = () => {
    setOtpSessionId(null);
    setOtpRequired(false);
    setEmailOtp("");
    setMobileOtp("");
    setOtpChannels({ email: false, mobile: true });
    setOtpStatus({ emailVerified: false, mobileVerified: false });
  };

  const isOtpBusy = isSendingOtp || isVerifyingOtp;

  // Redirect if already logged in (opening /register while authenticated)
  useEffect(() => {
    if (!isRegistered) {
      return;
    }
    if (skipRegisteredAutoRedirectRef.current) {
      return;
    }
    navigate(resolveAuthenticatedHomePath());
  }, [isRegistered, navigate]);

  useEffect(() => {
    const typeParam = searchParams.get("type")?.trim();
    if (typeParam === "partner") {
      setAccountType("partner");
    } else if (typeParam === "msme") {
      setAccountType("msme");
    }
  }, [searchParams]);

  useEffect(() => {
    const inviteEmail = searchParams.get("email")?.trim();
    const supplierInvite = searchParams.get("supplierInvite")?.trim();
    if (inviteEmail) {
      setFormData((prev) => (prev.email ? prev : { ...prev, email: inviteEmail }));
    }
    if (supplierInvite) {
      setAccountType("msme");
      storePendingSupplierInvite(supplierInvite);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedPlanId) {
      setPendingBillingPlanId(selectedPlanId);
    }
  }, [selectedPlanId]);

  useEffect(() => {
    try {
      const { password: _password, confirmPassword: _confirmPassword, agreeToTerms: _agree, ...rest } = formData;
      sessionStorage.setItem(REGISTER_DRAFT_KEY, JSON.stringify(rest));
    } catch {
      // Ignore quota / private-mode failures.
    }
  }, [formData]);

  const switchAccountType = (nextType: RegisterAccountType) => {
    setAccountType(nextType);
    setRegisterError(null);
    resetOtpState();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("type", nextType);
    setSearchParams(nextParams, { replace: true });
  };

  // Handle input changes
  const handleInputChange = (field: keyof RegisterFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validation function - match your User model requirements
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Password validation — match production API policy
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else {
      const passwordCheck = validatePassword(formData.password);
      if (!passwordCheck.valid) {
        newErrors.password = passwordCheck.message;
      }
    }

    // Confirm Password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = "Passwords must match";
    }

    // Role validation - enum: ['admin', 'view', 'msme']
    // if (!formData.role) {
    //   newErrors.role = "Role is required";
    // } else if (!["admin", "view", "msme"].includes(formData.role)) {
    //   newErrors.role = "Invalid role";
    // }

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    // Phone validation — Indian mobile (10 digits starting 6–9)
    const phoneDigits = toNationalIndianMobile(formData.phone);
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneDigits) {
      newErrors.phone = "Enter a valid 10-digit Indian mobile number";
    }

    // Terms validation
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms and conditions";
    }

    if (accountType === "partner") {
      if (!formData.organizationName.trim()) {
        newErrors.organizationName = "Organization name is required";
      }
      if (!formData.organizationType) {
        newErrors.organizationType = "Organization type is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const completeRegistration = async () => {
    setIsRegistering(true);
    const registeredEmail = formData.email;
    const registeredPassword = formData.password;
    try {
      if (accountType === "msme" && !signupGoal) {
        setStoredSignupGoal("baseline_footprint");
        setSignupGoal("baseline_footprint");
      }
      const normalizedPhone = toNationalIndianMobile(formData.phone);
      const payload =
        accountType === "partner"
          ? {
              email: formData.email,
              password: formData.password,
              role: "partner",
              profile: {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: normalizedPhone,
              },
              partnerProfile: {
                organizationName: formData.organizationName,
                organizationType: formData.organizationType,
              },
            }
          : {
              email: formData.email,
              password: formData.password,
              role: "msme",
              profile: {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: normalizedPhone,
              },
            };

      if (otpRequired && otpSessionId) {
        (payload as { otpSessionId?: string }).otpSessionId = otpSessionId;
      }

      const response = await ApiService.register(payload);

      if (!response?.success) {
        throw new Error(
          response?.message || "Registration failed. Please try again.",
        );
      }

      let shouldRedirectToLogin = false;

      if (accountType === "partner") {
        const apiKey = (response?.data?.partner as { apiKeyFull?: string })?.apiKeyFull;
        skipRegisteredAutoRedirectRef.current = true;
        try {
          await completeLoginFromResponse(response);
        } catch {
          const token = (response?.data as { token?: string })?.token;
          if (token) {
            localStorage.setItem("token", token);
          }
        }
        if (apiKey) {
          setPartnerApiKeyFull(apiKey);
          setPartnerWizardOpen(true);
          setRegisterSuccess(null);
        try { sessionStorage.removeItem(REGISTER_DRAFT_KEY); } catch { /* ignore */ }
          setIsRegistering(false);
          resetOtpState();
          return;
        }
        setRegisterSuccess(
          "Partner account created! You can now sign in to access your partner portal.",
        );
        shouldRedirectToLogin = true;
      } else {
        setAccountPathway("msme");
        try {
          skipRegisteredAutoRedirectRef.current = true;
          // Registration already verified the phone and issued a session.
          // Signing in again immediately asks for a second OTP and drops that session.
          let loginResult;
          try {
            loginResult = await completeLoginFromResponse(response);
          } catch {
            loginResult = await login(registeredEmail, registeredPassword, "msme");
          }
          if (loginResult.status === "authenticated") {
            if (loginResult.redirectPath) {
              navigate(loginResult.redirectPath);
              return;
            }
            const destination = await resolveMsmePostLoginDestination();
            const pendingPlanId = getPendingBillingPlanId();
            if (pendingPlanId && destination === "/dashboard") {
              clearPendingBillingPlanId();
              navigate(billingPathForPlanIntent(pendingPlanId));
              return;
            }
            navigate(destination);
            return;
          }
          skipRegisteredAutoRedirectRef.current = false;
          if (loginResult.status === "login_otp_required") {
            const pendingLoginOtp: PendingLoginOtpState = {
              sessionId: loginResult.sessionId,
              channels: resolveOtpChannels(loginResult.channels),
              email: registeredEmail,
              message:
                loginResult.message
                || "Account created. Enter the OTP sent to your mobile to finish signing in.",
            };
            const planQuery =
              selectedPlanId ? `?type=msme&plan=${encodeURIComponent(selectedPlanId)}` : "?type=msme";
            navigate(`/login${planQuery}`, { replace: true, state: { pendingLoginOtp } });
            return;
          }
          setRegisterSuccess(
            "Account created. Sign in and complete verification to continue.",
          );
          shouldRedirectToLogin = true;
        } catch {
          skipRegisteredAutoRedirectRef.current = false;
          setRegisterSuccess(
            "Account created. Sign in with your email and password to continue.",
          );
          shouldRedirectToLogin = true;
        }
      }
      setFormData({
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        phone: "",
        agreeToTerms: false,
        organizationName: "",
        organizationType: "integration_partner",
      });
      resetOtpState();

      if (shouldRedirectToLogin) {
        const planQuery =
          accountType === "msme" && selectedPlanId
            ? `&plan=${encodeURIComponent(selectedPlanId)}`
            : "";
        const loginPath =
          accountType === "partner" ? "/login?type=partner" : `/login?type=msme${planQuery}`;
        setTimeout(() => {
          navigate(loginPath);
        }, 2000);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Registration failed. Please try again.";
      // Let callers that need OTP gate handling rethrow after detecting this pattern.
      if (/otp verification is required/i.test(message)) {
        throw error instanceof Error ? error : new Error(message);
      }
      setRegisterError(message);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleRequestOtp = async () => {
    setIsSendingOtp(true);
    setRegisterError(null);
    try {
      const response = await ApiService.requestRegistrationOtp({
        email: formData.email,
        phone: toNationalIndianMobile(formData.phone),
      });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to request OTP");
      }

      const data = response?.data ?? {};
      const otpDisabled =
        data.otpRequired === false || data.otpRequired === "false";
      // OTP disabled for testing: create the account immediately.
      if (otpDisabled && !data.sessionId) {
        await completeRegistration();
        return;
      }

      if (!data.sessionId) {
        throw new Error(
          response?.message || "OTP session was not created. Please try again.",
        );
      }

      const channels = resolveOtpChannels(data.channels);
      setOtpRequired(true);
      setOtpSessionId(String(data.sessionId));
      setOtpChannels(channels);
      setEmailOtp("");
      setMobileOtp("");
      setOtpStatus({ emailVerified: false, mobileVerified: false });
      if (typeof window !== "undefined" && "scrollTo" in window) {
        // Avoid jsdom's unimplemented scrollTo noise in unit tests.
        if (!(window as Window & { __JSDOM__?: boolean }).__JSDOM__ && process.env.NODE_ENV !== "test") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
      if (typeof response?.message === "string" && response.message.trim()) {
        setRegisterSuccess(response.message);
      } else if (channels.email && channels.mobile) {
        setRegisterSuccess("OTP sent to your mobile number and email address.");
      } else if (channels.email) {
        setRegisterSuccess("OTP sent to your email. Enter it below to finish registration.");
      } else {
        setRegisterSuccess("OTP sent to your mobile. Enter it below to finish registration.");
      }
    } catch (error) {
      setRegisterError(
        error instanceof Error ? error.message : "Unable to send OTP.",
      );
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtpAndRegister = async () => {
    if (!otpSessionId) {
      setRegisterError("OTP session expired. Please request OTP again.");
      return;
    }

    const sharedOtp = otpChannels.email && otpChannels.mobile
      ? (mobileOtp || emailOtp).trim()
      : otpChannels.mobile
        ? mobileOtp.trim()
        : emailOtp.trim();

    if (!isValidRegistrationOtp(sharedOtp)) {
      setRegisterError("Enter the 6-digit OTP.");
      return;
    }

    setIsVerifyingOtp(true);
    setRegisterError(null);
    try {
      const response = await ApiService.verifyRegistrationOtp({
        sessionId: otpSessionId,
        otp: sharedOtp,
        ...(otpChannels.email ? { emailOtp: sharedOtp } : {}),
        ...(otpChannels.mobile ? { mobileOtp: sharedOtp } : {}),
      });

      if (!response?.success) {
        throw new Error(response?.message || "OTP verification failed");
      }

      setOtpStatus({
        emailVerified: Boolean(response?.data?.emailVerified),
        mobileVerified: Boolean(response?.data?.mobileVerified),
      });

      if (!response?.data?.verified) {
        throw new Error(
          response?.message || "Invalid OTP. Please check email and mobile OTP values.",
        );
      }

      setRegisterSuccess(null);
      await completeRegistration();
    } catch (error) {
      setRegisterError(
        error instanceof Error ? error.message : "OTP verification failed.",
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const preventPasswordFieldClipboard = (
    event: React.ClipboardEvent | React.DragEvent,
  ) => {
    event.preventDefault();
  };

  const preventPasswordFieldClipboardKeys = (event: React.KeyboardEvent) => {
    const key = event.key.toLowerCase();
    const isModifier = event.ctrlKey || event.metaKey;
    const isShiftInsert = event.shiftKey && key === "insert";
    if ((isModifier && (key === "v" || key === "c" || key === "x")) || isShiftInsert) {
      event.preventDefault();
    }
  };

  const formBusy = isRegistering || isOtpBusy;
  const submitButtonLabel = formBusy
    ? isSendingOtp
      ? "Sending OTP..."
      : isVerifyingOtp
        ? "Verifying OTP..."
        : accountType === "partner"
          ? "Creating partner account..."
          : "Creating Account..."
    : otpRequired
      ? "Verify OTP & Create Account"
      : accountType === "partner"
        ? "Register as partner"
        : "Create Account";
  const submitButtonIcon = <PersonAddOutlined />;

  const handleRegistrationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);
    if (!otpRequired) {
      setRegisterSuccess(null);
    }

    if (otpRequired) {
      await handleVerifyOtpAndRegister();
      return;
    }

    if (!validateForm()) {
      return;
    }

    // Request OTP first when enabled; request-otp returns otpRequired=false when disabled.
    await handleRequestOtp();
  };

  const maskedPhone = (() => {
    const digits = String(formData.phone || "").replace(/\D/g, "");
    if (digits.length < 4) {
      return formData.phone || "your mobile";
    }
    return `******${digits.slice(-4)}`;
  })();

  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        px: 2,
        overflowY: "auto",
        boxSizing: "border-box",
        ...(isDark
          ? {
              backgroundColor: theme.palette.background.default,
              backgroundImage: `radial-gradient(ellipse 120% 80% at 0% -20%, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 55%), radial-gradient(ellipse 100% 60% at 100% 0%, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 50%)`,
            }
          : {
              background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
            }),
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          background:
            theme.palette.mode === "light"
              ? "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"
              : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.common.white, 0.04)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.28 : 0.12)}`,
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <BrandLogoMark width={260} sx={{ mb: 2 }} />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {otpRequired ? "Verify OTP" : "Create Account"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {otpRequired
              ? "Enter the OTP you received to finish creating your account"
              : accountType === "partner"
                ? "Create your partner organization account"
                : "Create your company account — takes about 2 minutes"}
          </Typography>
          {!otpRequired && accountType === "msme" ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Partner organization?{" "}
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => switchAccountType("partner")}
                sx={{ verticalAlign: "baseline" }}
              >
                Register as a partner
              </Link>
            </Typography>
          ) : !otpRequired ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Running a factory or MSME?{" "}
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => switchAccountType("msme")}
                sx={{ verticalAlign: "baseline" }}
              >
                Register as a company
              </Link>
            </Typography>
          ) : null}
        </Box>

        {/* Success Alert */}
        {registerSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {registerSuccess}
          </Alert>
        )}

        {selectedPlanId && accountType === "msme" && !registerSuccess && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Your plan will be ready in Billing after you finish your company profile.
          </Alert>
        )}

        {/* Error Alert */}
        {registerError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {registerError}
          </Alert>
        )}

        <FormErrorSummary messages={Object.values(errors).filter(Boolean) as string[]} />

        {/* Registration Form */}
        <form onSubmit={handleRegistrationSubmit}>
          <Stack spacing={3}>
            {otpRequired ? (
              <>
                <Alert severity="info" data-testid="register-otp-step" sx={{ wordBreak: 'break-word' }}>
                  {otpChannels.email && otpChannels.mobile
                    ? `Enter the 6-digit OTP sent to ${formData.email} and ${maskedPhone}.`
                    : otpChannels.mobile
                      ? `Enter the 6-digit OTP sent to ${maskedPhone}.`
                      : `Enter the 6-digit OTP sent to ${formData.email}.`}
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  Registration details are saved. Verify OTP to create your account.
                </Typography>
                {otpChannels.email && !otpChannels.mobile ? (
                  <TextField
                    fullWidth
                    autoFocus
                    label="Email OTP"
                    placeholder="Enter 6-digit OTP"
                    value={emailOtp}
                    onChange={(e) =>
                      setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    autoComplete="one-time-code"
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 6,
                      "aria-label": "Email OTP",
                      "data-testid": "register-email-otp",
                      placeholder: "Enter 6-digit OTP",
                    }}
                    disabled={formBusy}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PinOutlined color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    autoFocus
                    label={otpChannels.email ? "OTP" : "Mobile OTP"}
                    placeholder="Enter 6-digit OTP"
                    value={mobileOtp}
                    onChange={(e) =>
                      setMobileOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    autoComplete="one-time-code"
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 6,
                      "aria-label": otpChannels.email ? "OTP" : "Mobile OTP",
                      "data-testid": "register-mobile-otp",
                      placeholder: "Enter 6-digit OTP",
                    }}
                    disabled={formBusy}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PinOutlined color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {otpChannels.email && otpChannels.mobile
                    ? `Email verified: ${otpStatus.emailVerified ? "Yes" : "No"} | Mobile verified: ${otpStatus.mobileVerified ? "Yes" : "No"}`
                    : otpChannels.mobile
                      ? `Mobile verified: ${otpStatus.mobileVerified ? "Yes" : "No"}`
                      : `Email verified: ${otpStatus.emailVerified ? "Yes" : "No"}`}
                </Typography>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={formBusy}
                  data-testid="register-otp-submit"
                  startIcon={
                    formBusy ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      submitButtonIcon
                    )
                  }
                  sx={{
                    py: 1.5,
                    background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #43a047 0%, #1b5e20 100%)",
                    },
                  }}
                >
                  {submitButtonLabel}
                </Button>
                <Button
                  type="button"
                  variant="text"
                  onClick={handleRequestOtp}
                  disabled={formBusy}
                  data-testid="register-otp-resend"
                >
                  Resend OTP
                </Button>
                <Button
                  type="button"
                  variant="text"
                  disabled={formBusy}
                  onClick={() => {
                    resetOtpState();
                    setRegisterSuccess(null);
                    setRegisterError(null);
                  }}
                  data-testid="register-otp-edit-details"
                >
                  Edit registration details
                </Button>
              </>
            ) : null}

            {!otpRequired && accountType === "msme" && (
              <FormControl fullWidth size="small">
                <InputLabel id="signup-goal-label">What do you need? (optional)</InputLabel>
                <Select
                  labelId="signup-goal-label"
                  label="What do you need? (optional)"
                  value={signupGoal || ""}
                  onChange={(e) => {
                    const raw = String(e.target.value || "");
                    const next = raw ? (raw as SignupGoalId) : null;
                    setSignupGoal(next);
                    setStoredSignupGoal(next);
                  }}
                  disabled={formBusy || otpRequired}
                >
                  <MenuItem value="">
                    <em>Skip for now — show me the basics</em>
                  </MenuItem>
                  {SIGNUP_GOAL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!otpRequired && accountType === "partner" && (
              <>
                <TextField
                  fullWidth
                  label="Organization name"
                  value={formData.organizationName}
                  onChange={(e) => handleInputChange("organizationName", e.target.value)}
                  error={!!errors.organizationName}
                  helperText={errors.organizationName}
                  disabled={formBusy}
                />
                <FormControl fullWidth error={!!errors.organizationType} disabled={formBusy}>
                  <InputLabel>Organization type</InputLabel>
                  <Select
                    value={formData.organizationType}
                    label="Organization type"
                    onChange={(e) =>
                      handleInputChange("organizationType", e.target.value as PartnerOrganizationType)
                    }
                  >
                    {PARTNER_ORGANIZATION_TYPES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.organizationType && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                      {errors.organizationType}
                    </Typography>
                  )}
                </FormControl>
              </>
            )}

            {!otpRequired && (
              <>
                {/* First Name Field */}
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  autoComplete="given-name"
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  disabled={formBusy}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Last Name Field */}
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  autoComplete="family-name"
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  disabled={formBusy}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Email Field */}
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  autoComplete="email"
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={formBusy}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Phone Field */}
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  autoComplete="tel"
                  error={!!errors.phone}
                  helperText={errors.phone || "10-digit Indian mobile number"}
                  disabled={formBusy}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password Field */}
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  autoComplete="new-password"
                  onPaste={preventPasswordFieldClipboard}
                  onCopy={preventPasswordFieldClipboard}
                  onCut={preventPasswordFieldClipboard}
                  onDrop={preventPasswordFieldClipboard}
                  onDragOver={preventPasswordFieldClipboard}
                  onKeyDown={preventPasswordFieldClipboardKeys}
                  error={!!errors.password}
                  helperText={errors.password || passwordPolicySummary()}
                  disabled={formBusy}
                  inputProps={{
                    "data-testid": "register-password",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          aria-label="toggle password visibility"
                          disabled={formBusy}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Confirm Password Field */}
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                  autoComplete="off"
                  onPaste={preventPasswordFieldClipboard}
                  onCopy={preventPasswordFieldClipboard}
                  onCut={preventPasswordFieldClipboard}
                  onDrop={preventPasswordFieldClipboard}
                  onDragOver={preventPasswordFieldClipboard}
                  onKeyDown={preventPasswordFieldClipboardKeys}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  disabled={formBusy}
                  inputProps={{
                    autoComplete: "off",
                    "data-testid": "register-confirm-password",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleToggleConfirmPasswordVisibility}
                          edge="end"
                          aria-label="toggle confirm password visibility"
                          disabled={formBusy}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Terms and Conditions */}
                <Box>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', minWidth: 0, mr: 0 }}
                    control={
                      <Checkbox
                        checked={formData.agreeToTerms}
                        onChange={(e) => handleInputChange("agreeToTerms", e.target.checked)}
                        disabled={formBusy}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ minWidth: 0 }}>
                        I agree to the{" "}
                        <Link
                          component={RouterLink}
                          to="/terms-of-service"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: "primary.main" }}
                        >
                          Terms and Conditions
                        </Link>{" "}
                        and{" "}
                        <Link
                          component={RouterLink}
                          to="/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: "primary.main" }}
                        >
                          Privacy Policy
                        </Link>
                      </Typography>
                    }
                  />
                  {errors.agreeToTerms && (
                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                      {errors.agreeToTerms}
                    </Typography>
                  )}
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={formBusy}
                  data-testid="register-submit"
                  startIcon={
                    formBusy ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      submitButtonIcon
                    )
                  }
                  sx={{
                    py: 1.5,
                    background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #43a047 0%, #1b5e20 100%)",
                    },
                    "&:disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  {submitButtonLabel}
                </Button>
              </>
            )}

          </Stack>
        </form>

        {/* Divider */}
        <Divider sx={{ my: 4 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>

        {/* Login Link */}
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Already have an account?
          </Typography>
          <Button
            component={RouterLink}
            to={accountType === "partner" ? "/login?type=partner" : "/login?type=msme"}
            variant="outlined"
            size="large"
            sx={{
              py: 1.5,
              borderColor: "success.main",
              color: "success.main",
              "&:hover": {
                borderColor: "success.dark",
                backgroundColor: alpha(theme.palette.success.main, isDark ? 0.14 : 0.06),
              },
            }}
          >
            Sign In
          </Button>
        </Box>

        {/* Footer Links */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3 }}
        >
          <Link
            component={RouterLink}
            to="/privacy-policy"
            variant="caption"
            color="text.secondary"
            sx={{
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Privacy Policy
          </Link>
          <Typography variant="caption" color="text.secondary">
            •
          </Typography>
          <Link
            component={RouterLink}
            to="/terms-of-service"
            variant="caption"
            color="text.secondary"
            sx={{
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Terms of Service
          </Link>
          {SUBSCRIPTION_PLANS_ENABLED ? (
            <>
              <Typography variant="caption" color="text.secondary">
                •
              </Typography>
              <Link
                component={RouterLink}
                to="/pricing"
                variant="caption"
                color="text.secondary"
                sx={{
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Pricing
              </Link>
            </>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            •
          </Typography>
          <Link
            component={RouterLink}
            to="/help"
            variant="caption"
            color="text.secondary"
            sx={{
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Help
          </Link>
        </Stack>

        {partnerApiKeyFull ? (
          <PartnerOnboardingWizard
            open={partnerWizardOpen}
            apiKeyFull={partnerApiKeyFull}
            organizationName={formData.organizationName || "Partner organization"}
            onComplete={async (webhookUrl) => {
              if (webhookUrl) {
                try {
                  const token = localStorage.getItem("token");
                  if (token) {
                    await axios.patch(
                      buildApiUrl("/partner-account/webhook"),
                      { webhookUrl },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                  }
                } catch {
                  /* optional */
                }
              }
            }}
          />
        ) : null}
      </Paper>
    </Box>
  );
};

export default UserRegister;